<template>
  <div></div>
</template>

<script>
import lodashFind from "lodash/find";
import lodashCloneDeep from "lodash/cloneDeep";

import {
  MODULE_PREFIX_MAP_CONTROLS,
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_MY_INCIDENTS,
  MODULE_PREFIX_USER,
} from "@/store";
import {
  GET_DETAILS_SIDEBAR,
  GET_MY_INCIDENTS_REFRESH_MAP,
  SAVE_PREFERENCES,
} from "@/store/actions.type";
import { addTileData, removeTileData } from "@/common/maps/features";
import { isLayerOnMapByMetadata } from "@/common/maps/layers";
import { MAP_TILE_SERVER_DYNAMIC } from "@/common/config";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import { waitForMapReady } from "@/common/maps/maps";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import {
  POINTS_SECTIONS,
  POINTS_SECTION_MY_INCIDENTS,
} from "@/common/constants/mapControls";

import { mapGetters } from "vuex";

export default {
  name: "MapControlsPointsMyIncidents",
  components: {},
  data: () => ({
    baseTileUrl: `${MAP_TILE_SERVER_DYNAMIC}my_incidents/{z}/{x}/{y}.pbf`,
  }),
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
      mainMapHijacker: MODULE_PREFIX_MAP + "mainMapHijacker",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
      mapControlParams: MODULE_PREFIX_MAP_CONTROLS + "params",
      refreshMap: MODULE_PREFIX_MY_INCIDENTS + "refreshMap",
      filters: MODULE_PREFIX_MY_INCIDENTS + "filters",
      preferences: MODULE_PREFIX_USER + "preferences",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    section() {
      return lodashFind(POINTS_SECTIONS, { id: POINTS_SECTION_MY_INCIDENTS });
    },
  },
  methods: {
    buildTileUrl() {
      const searchParams = new URLSearchParams();
      if (!!this.filters?.search) {
        searchParams.append("search", `%${this.filters.search}%`);
      }
      if (!!this.filters?.startDate) {
        searchParams.append("start", this.filters.startDate);
      }
      if (!!this.filters?.endDate) {
        searchParams.append("end", this.filters.endDate);
      }

      return searchParams.size > 0
        ? `${this.baseTileUrl}?${searchParams.toString()}`
        : this.baseTileUrl;
    },
    addToMap() {
      this.removeFromMap();
      if (this.mainMapHijacker) {
        return;
      }

      if (this.section?.mapLayerMetadata) {
        let layerOptions = {
          id: this.section.mapLayerMetadata,
          source: this.section.mapLayerMetadata,
          type: "symbol",
          layout: {
            "icon-image": ["concat", "myincidents_", ["get", "icon"]],
            "icon-size": 0.5,
          },
        };

        layerOptions["source-layer"] = "incidents";
        layerOptions["metadata"] = this.section.mapLayerMetadata;

        let source = {
          type: "vector",
          tiles: [this.buildTileUrl()],
          tolerance: 0,
        };

        let options = {
          id: this.section.mapLayerMetadata,
          source: source,
          layerOptions: [layerOptions],
          map: this.map,
          mapClickEvent: this.mapClickEvent,
        };

        addTileData(options);
      }
    },
    async mapClickEvent(e, features) {
      e.originalEvent.stopPropagation();

      if (features.length > 0) {
        this.detailsSidebar.features = features;
        this.detailsSidebar.visible = true;
        this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.myIncidents;
        this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.myIncidents;

        await this.$store.dispatch(
          MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
          this.detailsSidebar
        );

        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.myIncidents,
          EVENT_ACTIONS.clicked,
          `My Incident Map Click --- ${features[0].properties.gid}`
        );
      }
    },
    removeFromMap() {
      if (this.map && this.section?.mapLayerMetadata) {
        removeTileData({
          map: this.map,
          layerId: this.section.mapLayerMetadata,
          sourceId: this.section.mapLayerMetadata,
        });
      }
    },
    handleMapRefresh(force) {
      if (
        (this.refreshMap || force) &&
        this.map &&
        this.section?.mapLayerMetadata &&
        !this.mainMapHijacker
      ) {
        const isOnMap = isLayerOnMapByMetadata(
          this.map,
          this.section.mapLayerMetadata
        );
        const isSelected = this.mapControlParams?.mapPointsControlSelectedTypes?.includes(
          POINTS_SECTION_MY_INCIDENTS
        );
        if (isOnMap && !isSelected) {
          this.removeFromMap();
        } else if (!isOnMap && isSelected) {
          this.attemptAddToMap(force);
        }
        this.$store.dispatch(
          MODULE_PREFIX_MY_INCIDENTS + GET_MY_INCIDENTS_REFRESH_MAP,
          false
        );
        this.savePrefs(isSelected);
      }
    },
    async attemptAddToMap(moveMap, attemptCount = 0) {
      if (!this.map && attemptCount < 50) {
        setTimeout(() => {
          this.attemptAddToMap(moveMap, attemptCount + 1);
        }, 500);
      } else {
        await waitForMapReady(this.map);
        this.addToMap(moveMap);
      }
    },
    savePrefs(enabled) {
      const preferences = lodashCloneDeep(this.preferences);
      preferences.myIncidents = preferences.myIncidents || {};
      preferences.myIncidents.enabled = enabled;
      this.$store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, preferences);
    },
  },
  watch: {
    mainMapHijacker: {
      handler(newVal, oldVal) {
        this.handleMapRefresh(newVal !== oldVal);
      },
    },
    refreshMap: {
      handler() {
        this.handleMapRefresh("true");
      },
    },
    "mapControlParams.mapPointsControlEnabled": {
      handler() {
        this.handleMapRefresh("true");
      },
    },
    "mapControlParams.mapPointsControlSelectedTypes": {
      handler() {
        this.handleMapRefresh("true");
      },
      deep: true,
    },
  },
};
</script>

<style scoped></style>
